import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, Clock, CheckCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const YourOrders = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, [isAuthenticated, navigate]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/orders/my-orders`,
        { withCredentials: true }
      );
      setOrders(response.data || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      // Don't show error if user is not authenticated
      if (error.response?.status !== 401) {
        toast.error('Failed to load orders');
      }
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshOrders = async () => {
    toast.info('Refreshing orders...');
    await fetchOrders();
    toast.success('Orders updated!');
  };

  const getStatusIcon = (order) => {
    if (order.orderStatus === 'delivered') {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    } else if (order.waybill_number) {
      return <Truck className="w-5 h-5 text-blue-500" />;
    } else {
      return <Clock className="w-5 h-5 text-orange-500" />;
    }
  };

  const getStatusText = (order) => {
    if (order.orderStatus === 'delivered') {
      return 'Delivered';
    } else if (order.waybill_number) {
      return 'In Transit';
    } else {
      return 'Processing';
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50 py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-700 mb-2">Login Required</h2>
            <p className="text-gray-500 mb-6">Please login to view your orders</p>
            <button
              onClick={() => navigate('/login')}
              className="bg-rose-700 hover:bg-rose-800 text-white px-6 py-3 rounded-lg font-semibold"
            >
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50 py-16 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-rose-900 mb-2">Your Orders</h1>
              <p className="text-gray-600">Track and manage your orders</p>
            </div>
            <button
              onClick={refreshOrders}
              disabled={loading}
              className="flex items-center gap-2 bg-rose-700 hover:bg-rose-800 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-700 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-700 mb-2">No orders yet</h2>
            <p className="text-gray-500 mb-6">Start shopping to see your orders here!</p>
            <button
              onClick={() => navigate('/products')}
              className="bg-rose-700 hover:bg-rose-800 text-white px-6 py-3 rounded-lg font-semibold"
            >
              Browse Products
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Order Header */}
                <div className="bg-gradient-to-r from-rose-50 to-amber-50 px-6 py-4 border-b">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">
                        Order #{order.id.substring(0, 8).toUpperCase()}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Placed on {new Date(order.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(order)}
                      <span className="font-semibold text-gray-700">{getStatusText(order)}</span>
                    </div>
                  </div>
                </div>

                {/* Order Details */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Items */}
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-3">Order Items</h4>
                      <div className="space-y-2">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              {item.name} × {item.quantity}
                            </span>
                            <span className="text-gray-700 font-medium">₹{item.price * item.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Shipping Address */}
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-3">Delivery Address</h4>
                      <div className="text-sm text-gray-600">
                        <p className="font-medium text-gray-700">{order.shippingInfo.fullName}</p>
                        <p>{order.shippingInfo.address}</p>
                        <p>{order.shippingInfo.city}, {order.shippingInfo.state}</p>
                        <p>Pincode: {order.shippingInfo.pincode}</p>
                        <p>Phone: {order.shippingInfo.phone}</p>
                      </div>
                    </div>
                  </div>

                  {/* Payment & Total */}
                  <div className="border-t pt-4 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        order.paymentMethod === 'online' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {order.paymentMethod === 'online' ? 'Paid Online' : 'Cash on Delivery'}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Total Amount</p>
                      <p className="text-2xl font-bold text-rose-700">₹{order.totalAmount}</p>
                    </div>
                  </div>

                  {/* Tracking Section */}
                  <div className="mt-6 pt-6 border-t">
                    {order.waybill_number ? (
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border-2 border-green-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                              <p className="text-sm font-semibold text-green-700">
                                ✅ Delivery Started - Auto-Generated Tracking
                              </p>
                            </div>
                            <p className="text-xs text-gray-600 mb-2">Waybill Number (Auto-generated by Delhivery)</p>
                            <p className="text-xl font-mono font-bold text-gray-800 bg-white px-3 py-2 rounded border border-gray-200 inline-block">
                              {order.waybill_number}
                            </p>
                          </div>
                          <button
                            onClick={() => navigate(`/track-shipment`)}
                            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition ml-4"
                          >
                            <Truck className="w-4 h-4" />
                            Track Order
                          </button>
                        </div>
                        <p className="text-xs text-gray-600 mt-3">
                          🚚 Your order has been dispatched and is on its way! Use the tracking number above to see real-time updates.
                        </p>
                      </div>
                    ) : (
                      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg p-4 border-2 border-orange-200">
                        <div className="flex items-center gap-3">
                          <Clock className="w-6 h-6 text-orange-500" />
                          <div className="flex-1">
                            <p className="font-semibold text-gray-800 mb-1">⏳ Delivery arrangement in progress</p>
                            <p className="text-sm text-gray-600">
                              Your tracking number will be generated automatically once the shipment is created. 
                              Please check back in a few minutes or click the Refresh button above.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default YourOrders;
